import React, { useState, useEffect } from 'react';
import CRUDList from '../Common/CRUDList';
import DateInput from '../Common/DateInput';
import { passengerDocuments, passengers, countries } from '../../services/api';
import { passengerDocTypes, passengerSexTypes } from '../../constants/travelEnums';
import { formatDate } from '../../utils/dateFormat';

const passengerSexCodes = { 1: 'M', 2: 'F', 3: 'X' };
const passengerSexBadgeClasses = { 1: 'passenger-sex-male', 2: 'passenger-sex-female', 3: 'passenger-sex-unspecified' };

function DocumentForm({ formData, setFormData }) {
  const [passengersList, setPassengersList] = useState([]);
  const [countriesList, setCountriesList] = useState([]);

  useEffect(() => {
    loadPassengers();
    loadCountries();
  }, []);

  const loadPassengers = async () => {
    try {
      const response = await passengers.getAll();
      setPassengersList(response.data);
    } catch (err) {
      console.error('Failed to load passengers', err);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await countries.getAll();
      setCountriesList(response.data);
    } catch (err) {
      console.error('Failed to load countries', err);
    }
  };

  return (
    <>
      <div className="form-group">
        <label>Passenger *</label>
        <select
          value={formData.doc_passenger_id || ''}
          onChange={(e) => setFormData({ ...formData, doc_passenger_id: parseInt(e.target.value, 10) })}
          required
        >
          <option value="">Select passenger</option>
          {passengersList.map(p => (
            <option key={p.passenger_id} value={p.passenger_id}>
              {p.passenger_name || `Passenger ${p.passenger_id}`}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Document Type *</label>
        <select
          value={formData.doc_type || ''}
          onChange={(e) => setFormData({ ...formData, doc_type: parseInt(e.target.value) })}
          required
        >
          <option value="">Select type</option>
          {Object.entries(passengerDocTypes).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Document Type Code</label>
        <input
          type="text"
          value={formData.doc_type_code || ''}
          onChange={(e) => setFormData({ ...formData, doc_type_code: e.target.value.toUpperCase() })}
          maxLength={3}
        />
      </div>

      <div className="form-group">
        <label>Document Number *</label>
        <input
          type="text"
          value={formData.doc_number || ''}
          onChange={(e) => setFormData({ ...formData, doc_number: e.target.value })}
          required
        />
      </div>

      <div className="form-group">
        <label>First Name *</label>
        <input
          type="text"
          value={formData.doc_first_name || ''}
          onChange={(e) => setFormData({ ...formData, doc_first_name: e.target.value })}
          required
        />
      </div>

      <div className="form-group">
        <label>Middle Name</label>
        <input
          type="text"
          value={formData.doc_middle_name || ''}
          onChange={(e) => setFormData({ ...formData, doc_middle_name: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>Last Name</label>
        <input
          type="text"
          value={formData.doc_last_name || ''}
          onChange={(e) => setFormData({ ...formData, doc_last_name: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>Sex *</label>
        <select
          value={formData.doc_sex || ''}
          onChange={(e) => setFormData({ ...formData, doc_sex: parseInt(e.target.value) })}
          required
        >
          <option value="">Select sex</option>
          {Object.entries(passengerSexTypes).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Birth Date</label>
        <DateInput
          value={formData.doc_birth_date || ''}
          onChange={(value) => setFormData({ ...formData, doc_birth_date: value })}
        />
      </div>

      <div className="form-group">
        <label>Birth Place</label>
        <input
          type="text"
          value={formData.doc_birth_place || ''}
          onChange={(e) => setFormData({ ...formData, doc_birth_place: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>Citizenship *</label>
        <select
          value={formData.doc_citizenship || ''}
          onChange={(e) => setFormData({ ...formData, doc_citizenship: parseInt(e.target.value) })}
          required
        >
          <option value="">Select country</option>
          {countriesList.map(c => (
            <option key={c.country_id} value={c.country_id}>
              {c.country_name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Issuing Country *</label>
        <select
          value={formData.doc_issuing_country || ''}
          onChange={(e) => setFormData({ ...formData, doc_issuing_country: parseInt(e.target.value) })}
          required
        >
          <option value="">Select country</option>
          {countriesList.map(c => (
            <option key={c.country_id} value={c.country_id}>
              {c.country_name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Issuing Authority</label>
        <input
          type="text"
          value={formData.doc_issuing_authority || ''}
          onChange={(e) => setFormData({ ...formData, doc_issuing_authority: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>Issued At *</label>
        <DateInput
          value={formData.doc_issued_at || ''}
          onChange={(value) => setFormData({ ...formData, doc_issued_at: value })}
          required
        />
      </div>

      <div className="form-group">
        <label>Expiry At</label>
        <DateInput
          value={formData.doc_expiry_at || ''}
          onChange={(value) => setFormData({ ...formData, doc_expiry_at: value })}
        />
      </div>
    </>
  );
}

function PassengerDocumentsList() {
  return (
    <CRUDList
      title="Passenger Documents"
      api={passengerDocuments}
      columns={[
        'Passenger', 'Document type', 'Number', <span style={{ whiteSpace: 'nowrap' }}>Citizenship / Sex / Name</span>,
        <><div>Birthday</div><div>Birthday place</div></>, 'Issuing country/authority',
        <>Date of issue<br />Date of expiry</>
      ]}
      FormComponent={DocumentForm}
      defaultFormData={{
        doc_passenger_id: '',
        doc_type: 10,
        doc_type_code: '',
        doc_number: '',
        doc_first_name: '',
        doc_middle_name: '',
        doc_last_name: '',
        doc_sex: '',
        doc_birth_date: '',
        doc_birth_place: '',
        doc_citizenship: '',
        doc_issuing_country: '',
        doc_issuing_authority: '',
        doc_issued_at: '',
        doc_expiry_at: ''
      }}
      useOldStyle={true}
      tableClassName="passenger-documents-table"
      hideActionsHeader={true}
      renderActions={({ item, onEdit }) => (
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onEdit(item)}
          title="Edit document"
          aria-label="Edit document"
          style={{ padding: '6px 10px' }}
        >
          ✎
        </button>
      )}
      renderFormActions={({ editingId, onDelete }) => editingId ? (
        <button type="button" className="btn btn-danger" onClick={() => onDelete(editingId)}>
          Delete
        </button>
      ) : null}
      renderRow={(item) => (
        <>
          <td>{item.passenger_name || `Passenger ${item.doc_passenger_id}`}</td>
          <td>
            <span className="badge badge-info">{passengerDocTypes[item.doc_type]}</span>
            {item.doc_type_code && <span className="badge document-type-code-badge" style={{ marginLeft: '6px' }}>{item.doc_type_code}</span>}
          </td>
          <td>{item.doc_number}</td>
          <td>
            <div className="passenger-document-name-layout">
              <div className="passenger-document-badges">
                {item.citizenship_iso2 && <span className="badge citizenship-badge" title={item.citizenship_name || item.citizenship_iso2}>{item.citizenship_iso2}</span>}
                {item.doc_sex && <span className={`badge ${passengerSexBadgeClasses[item.doc_sex] || 'passenger-sex-unspecified'}`} title={passengerSexTypes[item.doc_sex] || 'Unspecified'}>{passengerSexCodes[item.doc_sex]}</span>}
              </div>
              <div className="passenger-document-name-parts">
              {item.doc_first_name && <div title="First name">{item.doc_first_name}</div>}
              {item.doc_middle_name && <div title="Middle name">{item.doc_middle_name}</div>}
              {item.doc_last_name && <div title="Last name">{item.doc_last_name}</div>}
              </div>
            </div>
          </td>
          <td>
            <div>{formatDate(item.doc_birth_date)}</div>
            {item.doc_birth_place && <div style={{ fontSize: 'calc(1em - 2px)', marginTop: '2px' }}>{item.doc_birth_place}</div>}
          </td>
          <td>
            {item.issued_country_iso2 && <span className="badge issued-country-badge" title={item.issued_country_name || item.issued_country_iso2}>{item.issued_country_iso2}</span>}
            {item.doc_issuing_authority && <span style={{ marginLeft: item.issued_country_iso2 ? '6px' : 0 }}>{item.doc_issuing_authority}</span>}
          </td>
          <td>
            <div>{formatDate(item.doc_issued_at)}</div>
            <div>{formatDate(item.doc_expiry_at)}</div>
          </td>
        </>
      )}
    />
  );
}

export default PassengerDocumentsList;
